import React, { useState } from "react";
import { Pin, Settings2 } from "lucide-react";
import { SECTION_COLORS, HOME_HELP, HOME_PIN_SUB } from "../../navigation/navConfig";
import { useEpingles } from "../../navigation/useEpingles";
import { useAujourdhui } from "../../navigation/useAujourdhui";
import EpinglesPicker from "./EpinglesPicker";
import AujourdhuiBand from "./AujourdhuiBand";

const MONTHS = ['JANVIER', 'FÉVRIER', 'MARS', 'AVRIL', 'MAI', 'JUIN', 'JUILLET', 'AOÛT', 'SEPTEMBRE', 'OCTOBRE', 'NOVEMBRE', 'DÉCEMBRE'];

const kickerOf = (meta, section, badges) => {
  const now = new Date();
  if (meta?.date) return now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }).toUpperCase();
  const base = meta?.kicker || section?.label || '';
  if (meta?.month) return `${MONTHS[now.getMonth()]} ${base}`.trim();
  const n = meta?.count ? badges?.[meta.count] : 0;
  if (n > 0) return `${n} ${base}`.trim();
  return base;
};

const effectifTexte = (e) => {
  if (!e) return null;
  const m = e.nb_medecins || 0, a = e.nb_assistants || 0;
  const blocs = e.blocs || [];
  const bloc = blocs.length ? `${blocs.length} bloc${blocs.length > 1 ? 's' : ''} (${blocs.map(b => `${b.type_titre || 'Bloc'} · ${b.medecin_nom || '?'}`).join(', ')})` : 'pas de bloc opératoire';
  return `Aujourd'hui : ${m} médecin${m > 1 ? 's' : ''} et ${a} assistant${a > 1 ? 's' : ''} · ${bloc}.`;
};

const NavHome = ({ tree, user, badges, onGo, onGoScreen, corrHabilite }) => {
  const heure = new Date().getHours();
  const salut = heure < 18 ? 'Bonjour' : 'Bonsoir';
  const { pins, keys, add, remove } = useEpingles(tree);
  const ajd = useAujourdhui(user, tree, corrHabilite);
  const effectif = effectifTexte(ajd.effectif);
  const [picker, setPicker] = useState(false);
  const help = HOME_HELP[user?.role] || "Une question sur un écran ?\nAppelez le secrétariat.";
  const pinSub = HOME_PIN_SUB[user?.role] || "Ce que vous ouvrez chaque matin.";

  return (
    <div className="opn-home" data-testid="nav-home">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="opn-home__title" data-testid="home-greeting">{salut} {user?.prenom}.</h1>
          <p className="opn-home__sub">Le cabinet en un coup d'œil, {heure < 12 ? 'ce matin' : heure < 18 ? 'cet après-midi' : 'ce soir'}.</p>
          {effectif && <p className="opn-home__sub" data-testid="home-effectif">{effectif}</p>}
        </div>
        <p className="text-right text-xs text-[var(--opn-muted)] whitespace-pre-line" data-testid="home-help">{help}</p>
      </div>

      <div className="opn-tiles mt-6" data-testid="home-tiles">
        {tree.sections.map((s, i) => { const I = s.icon; return (
          <button key={s.id} type="button" className="opn-tile" style={{ animationDelay: `${i * 50}ms`, '--sec': SECTION_COLORS[s.id] }} onClick={() => onGo(`${s.path}/${s.pages.find(p => !p.lien)?.path}`)} data-testid={`home-tile-${s.id}`}>
            <span className="opn-tile__icon">{s.emoji ? <span className="opn-emoji opn-emoji--xl" aria-hidden="true">{s.emoji}</span> : <I className="h-5 w-5" />}</span>
            <span className="opn-tile__label">{s.label}</span>
            <span className="opn-tile__count">{s.pages.length} page{s.pages.length > 1 ? 's' : ''}</span>
          </button>); })}
      </div>

      <AujourdhuiBand data={ajd} user={user} onGoScreen={onGoScreen} />

      <h2 className="opn-home__h2 flex items-center gap-3">Vos épingles. <span>{pinSub}</span>
        <button type="button" className="opn-tool ml-auto" title="Choisir mes épingles" onClick={() => setPicker(true)} data-testid="edit-epingles-btn"><Settings2 className="h-4 w-4" /></button>
      </h2>
      {pins.length > 0 && (
        <div className="opn-pins" data-testid="home-pins">
          {pins.map((p, i) => {
            const I = p.page.icon;
            const kick = kickerOf(p.meta, p.section, badges);
            const isAlert = p.meta?.count && badges?.[p.meta.count] > 0;
            const title = p.meta?.title || p.page.label;
            return (
              <button key={p.key} type="button" className="opn-pin" style={{ animationDelay: `${i * 60}ms`, '--sec': SECTION_COLORS[p.section?.id] }} onClick={() => onGo(p.target, p.ecran)} data-testid={`home-pin-${p.page.id}${p.ecran ? '-' + p.ecran.toLowerCase() : ''}`}>
                <div className={`opn-pin__kicker ${isAlert ? 'opn-pin__kicker--alert' : ''}`}>{kick}</div>
                <div className="opn-pin__title">{title}</div>
                <div className="opn-pin__desc">{p.meta?.desc || ''}</div>
                {p.page.emoji ? <span className="opn-pin__icon opn-emoji opn-emoji--lg" aria-hidden="true">{p.page.emoji}</span> : <I className="opn-pin__icon h-8 w-8" />}
              </button>);
          })}
        </div>
      )}

      <h2 className="opn-home__h2">Tout votre menu. <span>Ce que vous voyez, et rien d'autre.</span></h2>
      <div className="opn-lists" data-testid="home-menu">
        {tree.sections.map((s, i) => { const I = s.icon; return (
          <div key={s.id} className="opn-list" style={{ animationDelay: `${i * 50}ms`, '--sec': SECTION_COLORS[s.id] }}>
            <div className="opn-list__head">{s.emoji ? <span className="opn-emoji" aria-hidden="true">{s.emoji}</span> : <I className="h-4 w-4 opn-sec-icon" />}{s.label}</div>
            {s.pages.map(p => { const PI = p.icon; const n = badges?.[p.badge]; return (
              <button key={p.id} type="button" className="opn-list__item" onClick={() => onGo(p.lien || `${s.path}/${p.path}`)} data-testid={`home-menu-${s.id}-${p.id}`}>
                {p.emoji ? <span className="opn-emoji opn-emoji--sm" aria-hidden="true">{p.emoji}</span> : <PI />}{p.label}{p.lien && <Pin className="opacity-40" />}{n > 0 && <span className="opn-list__badge">{n}</span>}
              </button>); })}
          </div>); })}
      </div>
      {picker && <EpinglesPicker tree={tree} keys={keys} onAdd={add} onRemove={remove} onClose={() => setPicker(false)} />}
    </div>
  );
};

export default NavHome;
